<script setup lang="ts">
import { $customerList } from '@/api/users'
import { uniqBy } from 'lodash'
import type { CustomerSelect } from '@/models/customer'

interface IProps {
  modelValue?: any,
  label?: string,
  division?: any
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => {},
  label: 'Customer'
})

const emit = defineEmits<{
  'update:modelValue': (newValue: any) => void
}>()

const customers = ref<CustomerSelect[]>([])
const customersCurrentPage = ref<number>(1)
const customersPerPage: number = 10
const customersSearchStr = ref<string>('')
const isNextCustomersPageExist = ref<boolean>(false)
const customersLoading = ref<boolean>(false)


const getCustomers = async () => {
  customersLoading.value = true
  customers.value = []

  try {
    const res = await $customerList({
      customer_name__icontains: customersSearchStr.value,
      division: props.division ?? undefined,
      per_page: customersPerPage,
      page: customersCurrentPage.value,
    })

    // Append the new customers to the existing list
    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    customers.value = uniqBy([...customers.value, ...res.data.results], 'id')
    isNextCustomersPageExist.value = !!res.data.next
  }
  catch (e) {
    console.log(e)
  }
  finally {
    customersLoading.value = false
  }
}

const onFocusCustomerSelect = async () => {
  if (!customers.value.length && !props.modelValue) {
    customers.value = []
    customersCurrentPage.value = 1
    await getCustomers()
  }
}

const onLoadMoreCustomers = async () => {
  customersCurrentPage.value++ // Increment the current page to load the next page of customers
  await getCustomers()
}

const handleCustomerSearch = useDebounceFn((searchStr: string) => {
  customers.value = []
  customersCurrentPage.value = 1 // Set current page => 1 to load initial first page of customers
  customersSearchStr.value = searchStr
  getCustomers()
}, 500)

watch(() => props.division, () => {
  customers.value = []
  customersCurrentPage.value = 1
  getCustomers()
})
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...customers]"
    return-object
    :item-title="item => item?.name_with_subsidiary || item?.customer_name"
    item-value="id"
    :label="label"
    density="compact"
    no-filter
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusCustomerSelect"
    @update:search="handleCustomerSearch"
  >
    <template #no-data>
      <div
        v-if="!!customersLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Customers not found
      </div>
    </template>
    <template
      v-if="isNextCustomersPageExist"
      #append-item
    >
      <VBtn
        color="default"
        block
        :disabled="!!customersLoading"
        @click="onLoadMoreCustomers"
      >
        Load more...
        <VProgressCircular
          v-if="!!customersLoading"
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>
